import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import type { AdminItem } from '../adminSections';
import AdminItemPlaceholder from './AdminItemPlaceholder';

// Site light-blue accent — same value as the section-level expanded fill in
// AdminAccordionSection. Used here only for the focus ring so the keyboard
// or click focus state is in the site palette in both light and dark themes
// (replaces MUI's default white/black ButtonBase focus indicator).
const FOCUS_BLUE = '#4da6ff';

interface AdminItemAccordionProps {
  item: AdminItem;
  // Id of the currently-expanded item in this section. Only one item is open
  // at a time within a section (per D.4 § 6.5 S2).
  expandedItemId: string | null;
  onToggle: (itemId: string) => void;
}

const AdminItemAccordion = ({
  item,
  expandedItemId,
  onToggle,
}: AdminItemAccordionProps) => {
  const isExpanded = expandedItemId === item.id;

  const renderBody = () => {
    if (item.mode === 'live' && item.component) {
      const Component = item.component;
      const content = <Component />;
      return item.wrapper ? item.wrapper(content) : content;
    }
    return <AdminItemPlaceholder item={item} />;
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={() => onToggle(item.id)}
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
      // Card on tinted body: paper background, divider border, a soft 1px
      // shadow so the row feels like an elevated tile sitting on the
      // section's action.hover-tinted surface (set in AdminAccordionSection).
      sx={(theme) => ({
        mb: 1,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        // Stronger shadow so the card lifts more clearly off the tinted body.
        boxShadow: `0 1px 3px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.12)'}`,
        overflow: 'hidden',
        '&.Mui-expanded': {
          margin: '0 0 8px 0',
          boxShadow: `0 3px 6px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.14)'}`,
        },
        '&:before': { display: 'none' },
        '&:last-of-type': { mb: 0 },
      })}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`admin-item-${item.id}-content`}
        id={`admin-item-${item.id}-summary`}
        sx={{
          px: 2,
          minHeight: 44,
          '& .MuiAccordionSummary-content': { my: 1 },
          // Quiet surface; lift on hover, gently mark when expanded with a
          // subtle action.selected fill and a divider underline to separate
          // the header from the body. Matches the section-level accordion
          // pattern (AdminAccordionSection) one level down.
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-expanded': {
            backgroundColor: 'action.selected',
            borderBottom: 1,
            borderColor: 'divider',
          },
          '&.Mui-expanded:hover': {
            backgroundColor: 'action.selected',
          },
          // Override the default MUI ButtonBase focus indicator (white in dark,
          // dark in light) with a 2px inset site-blue ring. Keeps the keyboard
          // focus state on-brand and theme-consistent.
          '&.Mui-focusVisible': {
            boxShadow: `inset 0 0 0 2px ${FOCUS_BLUE}`,
            outline: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Typography variant="body2" fontWeight={500} noWrap>
            {item.label}
          </Typography>
          <Chip
            label={item.tableName}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          {item.mode === 'live' && (
            <Chip
              label="Live"
              color="success"
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {item.mode === 'pending-q2' && (
            <Chip
              label="Pending Q2"
              color="warning"
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>
        <Box sx={{ minHeight: 0 }}>{renderBody()}</Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AdminItemAccordion;
